import { useMemo } from 'react';
import { Clock3, LocateFixed, RefreshCw, Search, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBusTimes, useBusTimesActions } from './state';

type UnknownRecord = Record<string, unknown>;

function field(value: unknown, keys: string[]): unknown {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as UnknownRecord;
  return keys.map((key) => record[key]).find((candidate) => candidate !== undefined && candidate !== null);
}

function text(value: unknown, keys: string[], fallback: string): string {
  const found = field(value, keys);
  return typeof found === 'string' || typeof found === 'number' ? String(found) : fallback;
}

function id(value: unknown, keys: string[]): string {
  return text(value, keys, '');
}

function formatSeattleTime(value: unknown): { time: string; countdown: string } | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  const minutes = Math.round((date.getTime() - Date.now()) / 60000);
  const countdown = minutes <= 0 ? 'Due now' : `${minutes} min`;
  return { time, countdown };
}

function formatServiceDate(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return 'Service date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Service date unavailable';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function SelectField({
  label,
  value,
  disabled,
  options,
  dataKey,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  options: Array<{ id: string; label: string }>;
  dataKey: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-2">
      <span className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <select
        data-jr-key={dataKey}
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full appearance-none rounded-[14px] border border-border bg-card px-4 text-sm font-medium text-card-foreground shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-45"
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function BusTimesPage() {
  const snapshot = useBusTimes((current) => current);
  const actions = useBusTimesActions();
  const context = snapshot.context;
  const routes: unknown[] = Array.isArray(context.routes) ? context.routes : [];
  const selectedRoute = routes.find((route) => id(route, ['id']) === context.selectedRouteId);
  const directions: unknown[] =
    selectedRoute && Array.isArray(field(selectedRoute, ['directions']))
      ? (field(selectedRoute, ['directions']) as unknown[])
      : [];
  const selectedDirection = directions.find((direction) => id(direction, ['id']) === context.selectedDirectionId);
  const stops: unknown[] =
    selectedDirection && Array.isArray(field(selectedDirection, ['stops']))
      ? (field(selectedDirection, ['stops']) as unknown[])
      : [];
  const arrivals: unknown[] = Array.isArray(context.arrivals) ? context.arrivals.slice(0, 3) : [];

  const routeOptions = useMemo(
    () =>
      routes
        .map((route) => ({
          id: id(route, ['id', 'routeId', 'value']),
          label: text(route, ['name', 'shortName', 'routeName', 'number', 'id'], 'Route'),
        }))
        .filter((option) => option.id),
    [routes],
  );
  const directionOptions = useMemo(
    () =>
      directions
        .map((direction) => ({
          id: id(direction, ['id', 'directionId', 'value']),
          label: text(direction, ['name', 'label', 'title', 'directionName', 'id'], 'Direction'),
        }))
        .filter((option) => option.id),
    [context.selectedRouteId, directions],
  );
  const stopOptions = useMemo(
    () =>
      stops
        .map((stop) => ({
          id: id(stop, ['id', 'stopId', 'value']),
          label: text(stop, ['name', 'label', 'title', 'stopName', 'id'], 'Stop'),
        }))
        .filter((option) => option.id),
    [context.selectedDirectionId, context.selectedRouteId, stops],
  );

  const ready = snapshot.matches('ready');
  const canView = snapshot.can({ type: 'bus-times.view-schedule' });
  const canRefresh = snapshot.can({ type: 'bus-times.refresh-schedule' });
  const canRecall = snapshot.can({ type: 'bus-times.recall-stop' });
  const hasSelection = Boolean(context.selectedRouteId && context.selectedDirectionId && context.selectedStopId);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 py-6 sm:px-8 sm:py-9">
        <header className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-[14px] bg-primary text-primary-foreground">
              <LocateFixed className="size-5" />
            </div>
            <div>
              <p className="text-sm font-semibold tracking-[-0.02em]">Seattle Bus Times</p>
              <p className="text-xs text-muted-foreground">Scheduled service desk</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="size-2 rounded-full bg-accent" /> Seattle local time
          </div>
        </header>

        <section className="grid flex-1 gap-8 py-10 lg:grid-cols-[minmax(250px,0.72fr)_minmax(0,1.28fr)] lg:items-start lg:gap-12">
          <div>
            <h1 className="max-w-md text-4xl font-semibold leading-[1.05] tracking-[-0.04em] sm:text-5xl">
              Find the next bus, on your schedule.
            </h1>
            <p className="mt-5 max-w-md text-base leading-7 text-muted-foreground">
              Choose a route, direction, and stop to see the next scheduled arrivals. This is a timetable, not a live
              vehicle tracker.
            </p>
            <div className="mt-9 rounded-[18px] border border-border bg-card p-4 shadow-sm sm:p-5">
              <div className="mb-5 flex items-center gap-2 text-sm font-semibold">
                <Search className="size-4 text-primary" /> Build your stop
              </div>
              <div className="space-y-4">
                <SelectField
                  label="Route"
                  value={context.selectedRouteId ?? ''}
                  options={routeOptions}
                  dataKey="route-select"
                  onChange={actions.chooseRoute}
                />
                <SelectField
                  label="Direction"
                  value={context.selectedDirectionId ?? ''}
                  options={directionOptions}
                  disabled={!context.selectedRouteId}
                  dataKey="direction-select"
                  onChange={actions.chooseDirection}
                />
                <SelectField
                  label="Stop"
                  value={context.selectedStopId ?? ''}
                  options={stopOptions}
                  disabled={!context.selectedDirectionId}
                  dataKey="stop-select"
                  onChange={actions.chooseStop}
                />
                <Button
                  data-jr-key="view-schedule"
                  type="button"
                  className="mt-2 h-12 w-full rounded-[14px]"
                  disabled={!canView || !hasSelection}
                  onClick={actions.viewSchedule}
                >
                  View schedule
                </Button>
              </div>
              <Button
                data-jr-key="recall-stop"
                type="button"
                variant="ghost"
                className="mt-3 w-full text-muted-foreground"
                disabled={!canRecall}
                onClick={actions.recallStop}
              >
                Recall usual stop
              </Button>
            </div>
          </div>

          <section
            aria-live="polite"
            className="min-h-[360px] rounded-[18px] border border-border bg-card p-5 shadow-sm sm:p-7"
          >
            <div className="flex items-start justify-between gap-4 border-b border-border pb-5">
              <div>
                <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Arrival board
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
                  {hasSelection
                    ? text(
                        stops.find((stop) => id(stop, ['id', 'stopId', 'value']) === context.selectedStopId),
                        ['name', 'label', 'title', 'stopName'],
                        'Selected stop',
                      )
                    : 'Select a stop'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {formatServiceDate(context.refreshedAt ?? new Date().toISOString())}
                </p>
              </div>
              {ready && hasSelection ? (
                <Button
                  data-jr-key="refresh-schedule"
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-[12px]"
                  disabled={!canRefresh}
                  onClick={actions.refreshSchedule}
                >
                  <RefreshCw className="mr-2 size-4" /> Refresh
                </Button>
              ) : null}
            </div>
            {!ready ? (
              <div className="flex min-h-[260px] items-center justify-center text-center">
                <div>
                  <Clock3 className="mx-auto size-8 text-muted-foreground" />
                  <p className="mt-4 font-medium">Choose your stop to begin.</p>
                  <p className="mt-2 text-sm text-muted-foreground">Scheduled departures will appear here.</p>
                </div>
              </div>
            ) : arrivals.length === 0 ? (
              <div className="flex min-h-[260px] items-center justify-center text-center">
                <div>
                  <WifiOff className="mx-auto size-8 text-muted-foreground" />
                  <p className="mt-4 font-medium">No scheduled arrivals found</p>
                  <p className="mt-2 max-w-xs text-sm leading-6 text-muted-foreground">
                    There is no timetable data for this stop right now. Try refreshing or choose another stop.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                {arrivals.map((arrival, index) => {
                  const parsed = formatSeattleTime(
                    field(arrival, ['scheduledTime', 'arrivalTime', 'scheduledAt', 'time', 'departureTime']),
                  );
                  return (
                    <div
                      key={`${id(arrival, ['id', 'arrivalId']) || index}`}
                      className="flex items-center justify-between rounded-[18px] border border-border bg-background px-5 py-4"
                    >
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                          Scheduled
                        </p>
                        <p className="mt-1 text-3xl font-semibold tabular-nums tracking-[-0.04em] text-primary">
                          {parsed?.time ?? 'Unavailable'}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-muted-foreground">
                        {parsed?.countdown ?? 'Time unavailable'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </section>
        <footer className="border-t border-border pt-4 text-xs text-muted-foreground">
          Times shown are scheduled departures in Seattle local time.
        </footer>
      </div>
    </main>
  );
}

export default BusTimesPage;
